import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { PickersShortcutsItem } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { ReportType } from '../../../../../types';
import {
  DateRange,
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import GoogleLagTimesAlert from './google-lag-times-alert';
import { ReportFormValues } from '../reports-form-page';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { SectionHeader } from '../../../../shared/section-header';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface StepDateRangeProps {
  control: Control<ReportFormValues>;
  watch: UseFormWatch<ReportFormValues>;
  errors: FieldErrors<ReportFormValues>;
  selectedReportType: ReportType | null;
  clearErrors: UseFormClearErrors<ReportFormValues>;
  setValue: UseFormSetValue<ReportFormValues>;
}

export default function StepDateRange({
  control,
  watch,
  selectedReportType,
  errors,
  clearErrors,
  setValue,
}: StepDateRangeProps) {
  const { currentTier } = useUserSettings();

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: 'Last 24 Hours',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(1, 'day'), today];
      },
    },
    {
      label: 'Last 7 Days',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(7, 'day'), today];
      },
    },
    {
      label: 'Last 30 Days',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(30, 'day'), today];
      },
    },
    {
      label: 'Last 6 Months',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(6, 'month'), today];
      },
    },
    { label: 'Clear', getValue: () => [null, null] },
  ];

  const isXDaysSelected = watch('xDaysSelected');

  const enterpriseReportNames = [
    'activitiesSharedReportMyDrive',
    'activitiesSharedReportPublic',
    'activitiesSharedReportSharedDrive',
    'activitiesSharedReportPublicSharedDrives',
  ];

  const isEnterpriseReport = enterpriseReportNames.includes(
    selectedReportType?.name || ''
  );

  const canSelectPastSixMonths =
    isEnterpriseReport && currentTier === 'enterprise';

  return (
    <Stack gap={2} width={'100%'} alignItems={'left'}>
      <SectionHeader
        description={
          selectedReportType?.name === 'hangoutsMeet' ||
          selectedReportType?.name === 'EmailActivityReport'
            ? "Optionally provide a valid date range for your report's data. It is defaulted to the last 7 days. Or click the checkbox to report on a number of days going back from today."
            : "Optionally provide a valid date range for your report's data. It is defaulted to the last 7 days."
        }
      >
        Date Range
      </SectionHeader>

      <Stack gap={1}>
        {selectedReportType?.name === 'hangoutsMeet' ||
        selectedReportType?.name === 'EmailActivityReport' ? (
          <Stack flexDirection={'row'} alignItems="center">
            <Controller
              name="xDaysSelected"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  sx={{ alignSelf: 'flex-start' }}
                  control={
                    <Checkbox
                      checked={value}
                      onChange={(e) => {
                        onChange(e.target.checked);
                        if (!e.target.checked) {
                          setValue('reportOnLastXDays', 0);
                        }

                        setValue('dateRange', [
                          dayjs().subtract(7, 'days'),
                          dayjs(),
                        ]);
                        clearErrors('reportOnLastXDays');
                        clearErrors('dateRange');
                      }}
                    />
                  }
                  label="Past (X) Days"
                />
              )}
            />
            <Controller
              name="reportOnLastXDays"
              control={control}
              render={({ field }) => (
                <TextField
                  required={isXDaysSelected}
                  disabled={!isXDaysSelected}
                  type="number"
                  {...field}
                  inputProps={{
                    id: 'reportOnLastXDays',
                    min: 0,
                  }}
                  InputLabelProps={{
                    htmlFor: 'reportOnLastXDays',
                  }}
                  label="Last X number of days"
                  sx={{ width: 210 }}
                  onChange={(event) => {
                    const raw = event.target.value;

                    if (raw === '') {
                      field.onChange(null);
                      return;
                    }

                    const value = Number(raw);
                    field.onChange(isNaN(value) ? null : value);
                  }}
                  error={!!errors.reportOnLastXDays}
                  helperText={errors?.reportOnLastXDays?.message || ' '}
                />
              )}
            />
          </Stack>
        ) : null}
        <Controller
          name={'dateRange'}
          control={control}
          render={({ field }) => (
            <DateRangePicker
              {...field}
              sx={{ width: 357.5 }}
              label={'Report Date Range'}
              minDate={
                canSelectPastSixMonths
                  ? undefined
                  : dayjs().subtract(6, 'month')
              }
              disabled={
                (isXDaysSelected &&
                  selectedReportType?.name === 'hangoutsMeet') ||
                (selectedReportType?.name === 'EmailActivityReport' &&
                  isXDaysSelected)
              }
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                shortcuts: {
                  items: shortcutsItems,
                },
                textField: {
                  helperText: !errors.dateRange
                    ? 'Exclusive of Selected Dates'
                    : errors.dateRange.message,
                  error: !!errors.dateRange,
                },
                actionBar: { actions: [] },
              }}
            />
          )}
        />
      </Stack>
      {(selectedReportType?.name === 'hangoutsMeet' && isXDaysSelected) ||
      (selectedReportType?.name === 'EmailActivityReport' &&
        isXDaysSelected) ? (
        <GoogleLagTimesAlert />
      ) : null}
    </Stack>
  );
}
